import Evented from '../Evented';
import KeepAlive from './KeepAlive';
const RETRY_DELAY = 2000;
const SOCKET_OPEN = 1; // see websocket docs
const getParams = function (options) {
    return typeof options.params === 'function' ? options.params() : options.params || {};
};
const toQueryString = function (obj) {
    return Object.keys(obj).map(function (key) {
        return encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]);
    }).join('&');
};
export default class WebSocketTransport extends Evented {
    constructor(channelPath, options) {
        super();
        this.didSuccessfullyConnect = false;
        this.sendQueue = [];
        this.isConnected = false;
        this.uri = `${options.ssl ? 'wss' : 'ws'}:${options.host}${channelPath}`;
        this.options = options;
    }
    start() {
        try {
            this.socket = new WebSocket(`${this.uri}?${toQueryString(getParams(this.options))}`);
            if (this.socket) {
                this.socket.onopen = this.handleOpen.bind(this);
                this.socket.onclose = this.handleClose.bind(this);
                this.socket.onerror = this.handleError.bind(this);
                this.socket.onmessage = this.lookForInitialPong.bind(this);
            }
            else {
                throw new Error('Could not create web socket');
            }
        }
        catch (e) {
            this.emit('failed', 'connection', e);
        }
    }
    stop() {
        if (this.keepAlive) {
            this.keepAlive.clear();
        }
        if (this.socket) {
            this.socket.onopen = null;
            this.socket.onclose = null;
            this.socket.onerror = null;
            this.socket.onmessage = null;
            this.socket.close();
            delete this.socket;
        }
    }
    sendMessage(message) {
        if (this.socket && this.socket.readyState === SOCKET_OPEN) {
            this.socket.send(JSON.stringify(message));
        }
        else {
            this.sendQueue.push(message);
        }
    }
    handleOpen() {
        if (this.socket) {
            KeepAlive.sendPing(this.socket);
            while (this.sendQueue.length) {
                this.sendMessage(this.sendQueue.shift());
            }
        }
    }
    handleClose(event) {
        if (event && event.wasClean) {
            this.stop();
        }
        else {
            this.handleError(event);
        }
    }
    handleError(event) {
        this.stop();
        if (this.didSuccessfullyConnect) {
            if (this.isConnected) {
                this.isConnected = false;
                this.emit('down');
            }
            this.emit('reconnecting');
            if (this.keepAlive) {
                this.keepAlive.clear();
            }
            const preflight = this.options.preflight
                ? this.options.preflight()
                : Promise.resolve();
            const timer = new Promise((resolve) => { setTimeout(() => { resolve(); }, RETRY_DELAY); });
            Promise.all([preflight, timer]).then(() => { this.start(); });
        }
        else {
            this.emit('failed', 'connection', event);
        }
    }
    handleRegularMessage(event) {
        const frame = JSON.parse(event.data);
        if (this.keepAlive) {
            this.keepAlive.restart();
        }
        if (frame.error === 'Subscription Failed') {
            this.emit('failed', 'subscription', frame);
        }
        if (frame.pong === 'PONG') {
            this.emit('heartbeat');
        }
        else {
            this.emit('message', JSON.parse(frame.message), frame);
        }
    }
    lookForInitialPong(event) {
        if (this.socket) {
            this.keepAlive = new KeepAlive(this.socket);
            if (KeepAlive.isPong(event)) {
                this.handleConnectionUp();
            }
        }
    }
    handleConnectionUp() {
        if (this.socket) {
            this.socket.onmessage = this.handleRegularMessage.bind(this);
            if (!this.didSuccessfullyConnect) {
                this.didSuccessfullyConnect = true;
                this.emit('connected', 'initial');
            }
            if (!this.isConnected) {
                this.isConnected = true;
                this.emit('up');
            }
        }
    }
}
//# sourceMappingURL=WebSocketTransport.js.map